/**
 * Who is signed in, for the whole app.
 *
 * ## The rule this exists to hold: 401 offers a login, 403 does not
 *
 * ADR-0016 draws a hard line the UI has to respect. A **401** means we do not know who you
 * are — sign in again and it may work. A **403** means we know exactly who you are and
 * Tard has granted you nothing (`principal_unbound`), or withdrawn it (`principal_revoked`),
 * or your Merchant is not active (`merchant_not_active`). Sending those back to the login
 * screen produces a loop the user cannot escape and cannot understand, because signing in
 * again will succeed and change nothing.
 *
 * `auth.guard.ts` carries the same note on the server side: *"Not 401. ADR-0016: we know
 * exactly who this is, and their access was withdrawn — sending them to sign in again
 * would be advice that cannot possibly work."* This is the client half of that decision.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ApiRequestError,
  getSession,
  logout as postLogout,
  type SessionView,
} from '../api/client.js';

export type SessionState =
  /** First paint, before the probe answers. Never render a login screen here. */
  | { readonly status: 'loading' }
  | { readonly status: 'anonymous' }
  | { readonly status: 'authenticated'; readonly session: Extract<SessionView, { authenticated: true }> }
  /** Authenticated somewhere, refused here. Terminal — offering a retry would loop. */
  | { readonly status: 'refused'; readonly code: string }
  /** The API could not be reached at all. Distinct from "signed out". */
  | { readonly status: 'unreachable' };

export interface SessionContextValue {
  readonly state: SessionState;
  readonly refresh: () => Promise<void>;
  readonly signOut: () => Promise<boolean>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const session = await getSession();
      setState(session.authenticated ? { status: 'authenticated', session } : { status: 'anonymous' });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 403) {
        setState({ status: 'refused', code: error.body.error });
        return;
      }
      // A 401 from the probe should be impossible — it is `@Public()` — so anything left
      // is a transport or server problem, and saying "you are signed out" would be a
      // guess dressed as a fact.
      setState({ status: 'unreachable' });
    }
  }, []);

  const signOut = useCallback(async () => {
    let ikigaiSessionRemains = false;
    try {
      const result = await postLogout();
      ikigaiSessionRemains = result.ikigaiSessionRemains;
    } finally {
      // Even a failed logout drops the local view of the session: the cookie may well be
      // gone, and leaving the UI signed in after the user asked to leave is the worse
      // half of the two possible mistakes.
      setState({ status: 'anonymous' });
    }
    return ikigaiSessionRemains;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({ state, refresh, signOut }),
    [state, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
